import { ReactElement, useCallback, useMemo, useRef, useState } from 'react';

import { DeleteSampleConfirmDialog, ResponseCodeCounts } from './DeleteSampleConfirmDialog';

type DialogState = {
  open: boolean;
  responseCodeCounts: ResponseCodeCounts;
};

const INITIAL_STATE: DialogState = {
  open: false,
  responseCodeCounts: {},
};

type Resolver = (result: boolean) => void;

export type UseDeleteSampleConfirmDialogResult = {
  confirm: (responseCodeCounts: ResponseCodeCounts) => Promise<boolean>;
  dialog: ReactElement;
};

/**
 * Promise-based confirmation dialog for deleting samples that include rows
 * with response codes that need explicit user acknowledgement.
 *
 * Returns a `confirm()` function which displays the dialog and resolves with
 * `true` when the user confirms, `false` when the user cancels (or closes the
 * dialog). The returned `dialog` element must be mounted in the React tree.
 */
export const useDeleteSampleConfirmDialog = (): UseDeleteSampleConfirmDialogResult => {
  const [state, setState] = useState<DialogState>(INITIAL_STATE);
  const resolverRef = useRef<Resolver | null>(null);

  const close = useCallback((result: boolean) => {
    const resolver = resolverRef.current;
    resolverRef.current = null;
    setState(INITIAL_STATE);
    resolver?.(result);
  }, []);

  const confirm = useCallback(
    (responseCodeCounts: ResponseCodeCounts) =>
      new Promise<boolean>((resolve) => {
        resolverRef.current?.(false);
        resolverRef.current = resolve;
        setState({ open: true, responseCodeCounts });
      }),
    []
  );

  const onCancel = useCallback(() => close(false), [close]);
  const onConfirm = useCallback(() => close(true), [close]);

  const dialog = useMemo(
    () => (
      <DeleteSampleConfirmDialog
        open={state.open}
        responseCodeCounts={state.responseCodeCounts}
        onCancel={onCancel}
        onConfirm={onConfirm}
      />
    ),
    [state.open, state.responseCodeCounts, onCancel, onConfirm]
  );

  return useMemo(() => ({ confirm, dialog }), [confirm, dialog]);
};
